import { useEffect, useMemo, useState } from "react";
import { api, BoardSession, Manifest, TriggerEvent } from "../api";
import { splitRecent } from "../lib/recent";
import OlderToggle from "./OlderToggle";

type StonkyRuling = { verdict?: string; vetoed?: (number | string)[]; statement?: string };

function stonkyRuling(s: BoardSession): StonkyRuling | null {
  const d = s.deliberation as { stonky?: StonkyRuling } | null;
  return d?.stonky ?? null;
}

const SEVERITY_BADGE: Record<string, string> = {
  advisory: "bg-slate-700 text-slate-300",
  session: "bg-gold/20 text-gold",
  halt: "bg-down/20 text-down",
};

export default function Boardroom({ manifest }: { manifest: Manifest }) {
  const [sessions, setSessions] = useState<BoardSession[]>([]);
  const [triggers, setTriggers] = useState<TriggerEvent[]>([]);
  const [openSession, setOpenSession] = useState<string | null>(null);
  const [openMember, setOpenMember] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showOlder, setShowOlder] = useState(false);
  const shown = useMemo(() => splitRecent(sessions, (s) => s.started_at), [sessions]);

  useEffect(() => {
    void load();
    // Poll so an in-progress session (runs ~1-2 min in the background) shows
    // its result without a manual refresh.
    const iv = setInterval(load, 5000);
    return () => clearInterval(iv);
  }, []);

  async function load() {
    try {
      const s = await api.get<BoardSession[]>("/api/board/sessions");
      setSessions(s);
      setTriggers(await api.get<TriggerEvent[]>("/api/board/triggers"));
      // Keep the button spinning while the newest session is still running.
      setBusy(s.length > 0 && s[0].status === "running");
    } catch (e) { setError((e as Error).message); }
  }

  async function convene(kind: "triggered" | "morning") {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/board/convene?kind=${kind}`);  // returns at once
      await load();
    } catch (e) { setError((e as Error).message); }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-bold">Boardroom</h1>
        <div className="flex gap-2">
          <button className="btn" onClick={() => convene("morning")} disabled={busy}
                  title="Full catch-up session: expires stale directives and sets today's">
            {busy ? "In session…" : "Run today's session"}
          </button>
          <button className="btn btn-primary" onClick={() => convene("triggered")} disabled={busy}>
            {busy ? "In session…" : "Convene (intraday)"}
          </button>
        </div>
      </div>
      {busy && (
        <div className="card border-accent/40 text-sm text-slate-300 flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-accent animate-pulse" />
          The board is deliberating — perspectives, devil&rsquo;s advocate, chair, then Stonky ratifies.
          This runs about a minute; results appear below as they land.
        </div>
      )}
      {error && <div className="text-down text-sm">{error}</div>}

      {(showOlder ? sessions : shown.recent).map((s) => (
        <div key={s.id} className="card">
          <button className="w-full text-left flex items-center gap-3"
                  onClick={() => setOpenSession(openSession === s.id ? null : s.id)}>
            <span className={`badge ${s.status === "completed" ? "bg-up/20 text-up" : s.status === "failed" ? "bg-down/20 text-down" : "bg-gold/20 text-gold"}`}>
              {s.status}
            </span>
            <span className="font-semibold">{s.kind} session</span>
            <span className="text-slate-500 text-sm">{new Date(s.started_at).toLocaleString()}</span>
            <span className="ml-auto text-slate-500 text-sm">{s.directives.length} directives</span>
          </button>

          {openSession === s.id && (
            <div className="mt-4 space-y-4">
              {s.summary && <p className="text-sm text-slate-300">{s.summary}</p>}
              {s.error && <p className="text-sm text-down font-mono">{s.error}</p>}

              {stonkyRuling(s) && (
                <div className="border-l-2 border-gold pl-3 py-1">
                  <div className="text-gold text-xs font-mono uppercase tracking-widest mb-1">
                    Stonky&rsquo;s ruling — {stonkyRuling(s)!.verdict ?? "RATIFY"}
                    {(stonkyRuling(s)!.vetoed?.length ?? 0) > 0 &&
                      ` · vetoed: ${stonkyRuling(s)!.vetoed!.join(", ")}`}
                  </div>
                  <p className="text-sm text-slate-200 italic">&ldquo;{stonkyRuling(s)!.statement}&rdquo;</p>
                </div>
              )}

              {s.directives.length > 0 && (
                <div>
                  <h3 className="label">Ratified directives</h3>
                  <ol className="space-y-1">
                    {s.directives.map((d) => (
                      <li key={d.id} className="text-sm flex gap-2">
                        <span className="text-accent font-mono shrink-0">{d.seq}.</span>
                        <span className={d.status !== "active" ? "line-through text-slate-500" : ""}>{d.text}</span>
                      </li>
                    ))}
                  </ol>
                </div>
              )}

              {s.perspectives && (
                <div>
                  <h3 className="label">Member perspectives</h3>
                  <div className="space-y-2">
                    {Object.entries(s.perspectives).map(([key, p]) => (
                      <div key={key} className="border border-edge rounded-lg">
                        <button className="w-full text-left px-3 py-2 flex justify-between items-center text-sm"
                                onClick={() => setOpenMember(openMember === s.id + key ? null : s.id + key)}>
                          <span className="font-semibold">{manifest.agents[key]?.title ?? key}</span>
                          <span className="text-slate-500 font-mono text-xs">{p.model}</span>
                        </button>
                        {openMember === s.id + key && (
                          <div className="px-3 pb-3 text-sm text-slate-300 whitespace-pre-wrap">{p.text}</div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      ))}
      {shown.older.length > 0 && (
        <div className="card py-0">
          <OlderToggle count={shown.older.length} open={showOlder}
                       onToggle={() => setShowOlder(!showOlder)} noun="older sessions" />
        </div>
      )}
      {sessions.length === 0 && <div className="card text-slate-500 text-sm">No board sessions yet. Convene one, or wait for the morning schedule.</div>}

      <div className="card">
        <h2 className="font-semibold mb-2 text-sm text-slate-400 uppercase tracking-wider">Trigger timeline</h2>
        <div className="space-y-2">
          {triggers.map((t) => (
            <div key={t.id} className="flex items-center gap-3 text-sm">
              <span className={`badge ${SEVERITY_BADGE[t.severity] ?? "bg-slate-700"}`}>{t.severity}</span>
              <span className="text-slate-500 font-mono text-xs">{t.stream}</span>
              <span>{t.title}</span>
              <span className="ml-auto text-slate-600 text-xs">{new Date(t.created_at).toLocaleString()}</span>
            </div>
          ))}
          {triggers.length === 0 && <div className="text-slate-600 text-sm">no triggers filed</div>}
        </div>
      </div>
    </div>
  );
}
